import ReactDOM from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { Maximize2 } from 'lucide-react';

// Floating "job running in background" pill, mounted via portal so it
// stays visible when the ImportFlow modal is minimized.
export const BackgroundJobPill = ({ batch, onRestore }) => {
  const [container] = useState(() => document.createElement('div'));

  useEffect(() => {
    document.body.appendChild(container);
    return () => {
      try {
        document.body.removeChild(container);
      } catch {
        /* container already removed */
      }
    };
  }, [container]);

  const percent = useMemo(() => {
    const total = batch?.counts?.total ?? 0;
    const done = batch?.counts?.processed ?? 0;
    if (!total) return 0;
    return Math.min(100, Math.round((done / total) * 100));
  }, [batch]);

  const label = useMemo(() => {
    if (!batch) return 'Import running…';
    const done = batch?.counts?.processed ?? 0;
    const total = batch?.counts?.total ?? 0;
    return `Batch #${batch.id} · ${done}/${total}`;
  }, [batch]);

  return ReactDOM.createPortal(
    <button
      type="button"
      data-testid="background-job-pill"
      onClick={onRestore}
      className="fixed bottom-6 right-6 z-[60] flex items-center gap-3 pl-4 pr-3 py-2 rounded-full bg-purpleColor text-white shadow-lg hover:shadow-xl transition-shadow"
      aria-label="Open running import"
    >
      <span className="relative flex items-center justify-center w-8 h-8">
        <svg width="32" height="32" viewBox="0 0 32 32">
          <circle
            cx="16"
            cy="16"
            r="13"
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="3"
            fill="none"
          />
          <circle
            cx="16"
            cy="16"
            r="13"
            stroke="white"
            strokeWidth="3"
            fill="none"
            strokeDasharray={`${(percent / 100) * 2 * Math.PI * 13} 999`}
            strokeLinecap="round"
            transform="rotate(-90 16 16)"
            style={{ transition: 'stroke-dasharray 300ms ease-out' }}
          />
        </svg>
        <span className="absolute text-[9px] font-semibold">{percent}%</span>
      </span>
      <span className="flex flex-col items-start">
        <span className="text-xs font-medium">{label}</span>
        <span className="text-[10px] opacity-80">Tap to resume</span>
      </span>
      <Maximize2 size={14} />
    </button>,
    container
  );
};
